<template>
    <div class="qselect dropdown" :class="{ show: open }" ref="root">
        <button
            type="button"
            class="custom-select qselect__trigger"
            ref="trigger"
            :disabled="disabled"
            @click="toggle"
        ><span>{{ selectedLabel }}</span></button>
        <div class="dropdown-menu qselect__menu" :class="{ show: open }" :style="menuStyle">
            <div v-if="searchable" class="qselect__search">
                <input
                    type="text"
                    ref="search"
                    v-model="search"
                    placeholder="Buscar..."
                    @click.stop
                    @keydown.esc="close"
                />
            </div>
            <a
                v-if="placeholder !== null"
                href="#"
                class="dropdown-item"
                :class="{ active: value === null }"
                @click.prevent="select(null)"
            >{{ placeholder }}</a>
            <a
                v-for="opt in filteredOptions"
                :key="opt.value"
                href="#"
                class="dropdown-item"
                :class="{ active: opt.value === value }"
                @click.prevent="select(opt.value)"
            >{{ opt.label }}</a>
            <div v-if="searchable && filteredOptions.length === 0" class="qselect__empty">Sin resultados</div>
        </div>
    </div>
</template>

<script>
// Combobox propio para poder stylear la lista de opciones con los colores de
// Qupro: un <select> nativo delega el render del popup al SO/navegador y no
// se puede personalizar (ver docs/superpowers/specs del 2026-10-28).
//
// El menú se posiciona con position:fixed calculado a mano (no CSS absolute
// relativo al trigger): si el QSelect vive dentro de un contenedor con
// overflow (navbar con scroll horizontal, tabla con overflow:hidden, etc.)
// un popup absolute queda recortado ahí. Un <select> nativo no tiene este
// problema porque el SO lo dibuja por fuera del documento.
export default {
  name: 'QSelect',
  props: {
    options: { type: Array, default: () => [] }, // [{ value, label }] — label se renderiza como texto plano (escapado)
    value: { default: null },
    placeholder: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false }, // agrega un input de filtro arriba de las opciones
  },
  data() {
    return { open: false, menuStyle: {}, search: '' };
  },
  computed: {
    selectedLabel() {
      const match = this.options.find(o => o.value === this.value);
      if (match) return match.label;
      return this.placeholder || '';
    },
    filteredOptions() {
      if (!this.searchable || !this.search.trim()) return this.options;
      const q = this.search.trim().toLowerCase();
      // label puede traer HTML (ej. indentación de subcategorías): se busca
      // sobre el texto plano, no sobre las etiquetas.
      return this.options.filter(o => this.stripHtml(o.label).toLowerCase().includes(q));
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('scroll', this.handleWindowScroll, true);
    window.removeEventListener('resize', this.updatePosition);
  },
  methods: {
    stripHtml(str) {
      return String(str).replace(/<[^>]*>/g, '');
    },
    toggle() {
      if (this.disabled) return;
      if (this.open) {
        this.close();
      } else {
        this.openMenu();
      }
    },
    openMenu() {
      this.updatePosition();
      this.open = true;
      this.search = '';
      if (this.searchable) {
        this.$nextTick(() => {
          if (this.$refs.search) this.$refs.search.focus();
        });
      }
      window.addEventListener('scroll', this.handleWindowScroll, true);
      window.addEventListener('resize', this.updatePosition);
    },
    updatePosition() {
      const rect = this.$refs.trigger.getBoundingClientRect();
      this.menuStyle = {
        position: 'fixed',
        top: (rect.bottom + 2) + 'px',
        left: rect.left + 'px',
        minWidth: rect.width + 'px',
        // deja crecer el menú para labels largos (ej. categorías anidadas),
        // pero sin pasarse del borde derecho de la pantalla
        maxWidth: (window.innerWidth - rect.left - 16) + 'px',
      };
    },
    close() {
      if (!this.open) return;
      this.open = false;
      window.removeEventListener('scroll', this.handleWindowScroll, true);
      window.removeEventListener('resize', this.updatePosition);
    },
    handleWindowScroll(e) {
      // El scroll dentro del propio menú (lista de opciones) no debe tocar
      // nada: ya lo maneja el overflow del menú.
      if (this.$refs.root && this.$refs.root.contains(e.target)) return;
      // El scroll de fondo (navbar, tabla, body) ya no cierra el combo:
      // antes esto molestaba porque se cerraba solo al scrollear por fuera.
      // En cambio seguimos al trigger para que el menú no quede desalineado.
      this.updatePosition();
    },
    select(val) {
      this.close();
      this.$emit('input', val);
    },
    handleDocumentClick(e) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(e.target)) {
        this.close();
      }
    },
  },
};
</script>
